import type { LocalDate } from '@konvert/shared';

/**
 * Calendar arithmetic on `YYYY-MM-DD` strings. Everything here runs on UTC
 * millis, so daylight saving transitions cannot shift a civil date (SPEC 5.6).
 */

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export interface CivilDate {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
}

export function parseLocalDate(date: LocalDate): CivilDate {
  const m = DATE_RE.exec(date);
  if (!m) throw new RangeError(`invalid local date: ${date}`);
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const normalized = formatCivil({ year, month, day });
  if (normalized !== date) throw new RangeError(`invalid local date: ${date}`);
  return { year, month, day };
}

export function isLocalDate(value: string): boolean {
  try {
    parseLocalDate(value);
    return true;
  } catch {
    return false;
  }
}

function formatCivil(c: CivilDate): LocalDate {
  return toLocalDate(Date.UTC(c.year, c.month - 1, c.day));
}

export function toUtcMillis(date: LocalDate): number {
  const { year, month, day } = parseLocalDate(date);
  return Date.UTC(year, month - 1, day);
}

export function toLocalDate(utcMillis: number): LocalDate {
  const d = new Date(utcMillis);
  const y = String(d.getUTCFullYear()).padStart(4, '0');
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const DAY_MS = 86_400_000;

export function addDays(date: LocalDate, days: number): LocalDate {
  return toLocalDate(toUtcMillis(date) + days * DAY_MS);
}

export function diffDays(a: LocalDate, b: LocalDate): number {
  return Math.round((toUtcMillis(a) - toUtcMillis(b)) / DAY_MS);
}

export function compareDates(a: LocalDate, b: LocalDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function isBefore(a: LocalDate, b: LocalDate): boolean {
  return a < b;
}

export function isAfter(a: LocalDate, b: LocalDate): boolean {
  return a > b;
}

/** ISO weekday: 1 = Monday … 7 = Sunday. */
export function isoWeekday(date: LocalDate): number {
  const dow = new Date(toUtcMillis(date)).getUTCDay(); // 0 = Sunday
  return dow === 0 ? 7 : dow;
}

export function startOfWeek(date: LocalDate, weekStartsOn = 1): LocalDate {
  const current = isoWeekday(date);
  const delta = (current - weekStartsOn + 7) % 7;
  return addDays(date, -delta);
}

export function startOfMonth(date: LocalDate): LocalDate {
  const { year, month } = parseLocalDate(date);
  return toLocalDate(Date.UTC(year, month - 1, 1));
}

export function startOfYear(date: LocalDate): LocalDate {
  const { year } = parseLocalDate(date);
  return toLocalDate(Date.UTC(year, 0, 1));
}

export function addMonths(date: LocalDate, months: number): LocalDate {
  const { year, month, day } = parseLocalDate(date);
  const target = new Date(Date.UTC(year, month - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  return toLocalDate(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(day, lastDay)),
  );
}

export function addYears(date: LocalDate, years: number): LocalDate {
  const { year, month, day } = parseLocalDate(date);
  const lastDay = new Date(Date.UTC(year + years, month, 0)).getUTCDate();
  return toLocalDate(Date.UTC(year + years, month - 1, Math.min(day, lastDay)));
}

/** End of the period that starts at `periodStart`, inclusive. */
export function endOfPeriod(
  periodStart: LocalDate,
  kind: 'day' | 'week' | 'month' | 'year',
): LocalDate {
  switch (kind) {
    case 'day':
      return periodStart;
    case 'week':
      return addDays(periodStart, 6);
    case 'month':
      return addDays(addMonths(startOfMonth(periodStart), 1), -1);
    case 'year':
      return addDays(addYears(startOfYear(periodStart), 1), -1);
  }
}

export function datesBetween(from: LocalDate, to: LocalDate): LocalDate[] {
  const out: LocalDate[] = [];
  for (let d = from; compareDates(d, to) <= 0; d = addDays(d, 1)) out.push(d);
  return out;
}
